import { defineStore } from 'pinia';
import { Api } from '@/Api';
import type { Entitlements, EntitlementCapabilities, EntitlementLimits, EntitlementQuotas, EntitlementsResponse } from '@/types/entitlements';

export interface EntitlementsState {
  capabilities: EntitlementCapabilities;
  limits: EntitlementLimits;
  quotas: EntitlementQuotas;
  isLoaded: boolean;
  isLoading: boolean;
  error: string | null;
}

export const useEntitlementsStore = defineStore('entitlementsStore', {
  state: (): EntitlementsState => ({
    capabilities: {},
    limits: {},
    quotas: {},
    isLoaded: false,
    isLoading: false,
    error: null,
  }),

  actions: {
    /**
     * Fetches entitlements from the API.
     * By default, uses cached data. Pass forceRefresh=true to bypass cache.
     *
     * @param forceRefresh - When true, bypasses cache and fetches fresh data
     */
    async fetchEntitlements(forceRefresh: boolean = false) {
      // Skip if already loaded and not forcing refresh
      if (this.isLoaded && !forceRefresh) {
        return;
      }

      this.isLoading = true;
      this.error = null;

      try {
        const response: EntitlementsResponse = await Api.entitlements.get(forceRefresh);

        this.$patch({
          capabilities: response.data.capabilities.attributes,
          limits: response.data.limits.attributes,
          quotas: response.data.quotas.attributes,
          isLoaded: true,
        });
      } catch (err) {
        this.error = err instanceof Error ? err.message : 'Failed to load entitlements';
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Refreshes entitlements by fetching fresh data from the API.
     * Always bypasses cache.
     */
    async refreshEntitlements() {
      return this.fetchEntitlements(true);
    },

    /**
     * Resets the store to its initial state.
     * Useful for logout scenarios.
     */
    resetState() {
      this.$reset();
    },
  },

  getters: {
    /**
     * Returns all entitlements as a flat object for convenience.
     */
    all: (state): Entitlements => ({
      capabilities: state.capabilities,
      limits: state.limits,
      quotas: state.quotas,
    }),

    /**
     * Check if user has a specific capability.
     * Returns a function for use in templates: hasCapability("sso")
     */
    hasCapability:
      state =>
      (key: keyof EntitlementCapabilities): boolean => {
        return state.capabilities[key] === true;
      },

    /**
     * Alias for hasCapability — shorthand for use in components.
     */
    can:
      state =>
      (key: keyof EntitlementCapabilities): boolean => {
        return state.capabilities[key] === true;
      },

    /**
     * Get a specific limit value.
     * Returns null for unlimited, number for limited.
     * Returns a function for use in templates: getLimit("maxVideoLength")
     */
    getLimit:
      state =>
      (key: keyof EntitlementLimits): number | null | undefined => {
        return state.limits[key];
      },

    /**
     * Get a specific quota with used/remaining values.
     * Returns a function for use in templates: getQuota("monthlyUploads")
     */
    getQuota: state => (key: keyof EntitlementQuotas) => {
      return state.quotas[key];
    },

    /**
     * Check if an action is allowed within quota.
     * Returns true if remaining > amount or if quota is unlimited (null).
     */
    quotaAllows:
      state =>
      (key: keyof EntitlementQuotas, amount: number = 1): boolean => {
        const quota = state.quotas[key];
        if (!quota) {
          return true;
        }
        if (quota.remaining === null) {
          return true;
        }
        return quota.remaining >= amount;
      },

    /**
     * Check if a quota is exhausted (remaining === 0).
     */
    quotaExhausted:
      state =>
      (key: keyof EntitlementQuotas): boolean => {
        const quota = state.quotas[key];
        if (!quota) {
          return false;
        }
        if (quota.remaining === null) {
          return false;
        }
        return quota.remaining === 0;
      },
  },
});
